import React, { useState, useEffect, useCallback, useMemo } from 'react';
import CssBaseline from '@material-ui/core/CssBaseline';
import Button from '@material-ui/core/Button';

import { PreLoader } from 'app/components/PreLoader';

import { Helmet } from 'react-helmet-async';
import Typography from '@material-ui/core/Typography';
import Container from '@material-ui/core/Container';
import { useTranslation } from 'react-i18next';
import { messages } from './messages';

import { makeStyles } from '@material-ui/core/styles';

import ConfigParamContext from '../../../../../contexts/config/admin/ConfigParam';
import {
  AppBar,
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tab,
  Tabs,
} from '@material-ui/core';
import ParamForm from './ParamForm';
import GroupSection from './GroupSection';

import PropTypes from 'prop-types';
import SwipeableViews from 'react-swipeable-views';

const useStyles = makeStyles(theme => ({
  sectionOutline: {
    width: '60%',
    border: '1px solid #aaffaa',
    background: '#efefef',
    padding: '10px 20px',
    margin: '0 auto',
    marginTop: '10px',
  },
  preloader: {
    margin: '0 auto',
    color: theme.palette.primary.main,
  },
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    color: theme.palette.secondary.light,
    background: theme.palette.primary.main,
  },
  actionButton: {
    marginRight: theme.spacing(2),
    color: theme.palette.secondary.light,
    background: theme.palette.primary.main,
    position: 'absolute',
    right: 0,
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#00077',
  },
  moduleTab: {},
  groupTab: {
    background: '#ffffff',
    color: theme.palette.primary.dark,
  },
  subgroupTab: {
    color: '#000000',
    background: '#ffffff',
    '& .MuiButtonBase-root': {
      color: '#000000',
      background: '#ffffff',
      fontWeight: 'bold',
    },
    '& .Mui-selected': {
      color: '#ffffff',
      background: theme.palette.primary.main,
    },
    '& .MuiTab-wrapper': {
      whiteSpace: 'nowrap',
      padding: '0px 17px',
    },
  },
}));

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

function VerticalTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <div>{children}</div>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    'aria-controls': `full-width-tabpanel-${index}`,
  };
}

function verticalA11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

export function ConfigParamDashBoardPage() {
  const [value, setValue] = React.useState(0);
  const [groupValue, setGroupValue] = React.useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeGroup = (event, newValue) => {
    setGroupValue(newValue);
  };

  const handleChangeIndex = index => {
    setValue(index);
  };

  const [open, setOpen] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState('');
  const [alertMessage, setAlertMessage] = React.useState('');
  const [showPreloader, setShowPreloader] = useState(false);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  const backdropRef = React.createRef();

  const [configParamTable, setConfigParamTable] = useState([] as unknown[]);

  const { dashboard, updateParamValue } = React.useContext(ConfigParamContext);

  interface module {
    module: string;
    groups: {
      group: string;
      subgroups: { subgroup: string; params: unknown[] }[];
      params: unknown[];
    }[];
    params: unknown[];
  }

  let matrix: module[] = useMemo(() => [], []);

  const fixParamTable = React.useCallback(
    table => {
      let arrSG: { subgroup: string; params: unknown[] }[] = [];
      let params: unknown[] = [];
      let prevModule = '';
      let prevGroup = '';
      let prevSubgroup = '';
      let i = 0;

      function parseSubgroupInModule(lenMatrix: number) {
        const lenGroup = matrix[lenMatrix - 1].groups.length;
        if (prevSubgroup !== table[i].subgroup) {
          // Módulo nuevo, grupo grupo, subgrupo nuevo
          arrSG = [];
          if (table[i].subgroup && table[i].subgroup !== '') {
            const subgroup = {
              subgroup: table[i].subgroup as string,
              params: [],
            };
            matrix[lenMatrix - 1].groups[lenGroup - 1].subgroups.push(subgroup);
          }
          prevSubgroup = table[i].subgroup;
        } else {
          if (lenGroup <= 0) {
            matrix[lenMatrix - 1].params.push(table[i]);
          } else {
            matrix[lenMatrix - 1].groups[lenGroup - 1].params.push(table[i]);
          }
        }
      }

      function parseSubgroupInGroup(lenMatrix: number) {
        const lenGroup = matrix[lenMatrix - 1].groups.length;
        if (prevSubgroup !== table[i].subgroup) {
          // Continua el módulo, continua el grupo, subgrupo nuevo
          arrSG = [];
          params = [];
          params.push(table[i]);
          const subgroup = {
            subgroup: table[i].subgroup as string,
            params,
          };
          matrix[lenMatrix - 1].groups[lenGroup - 1].subgroups.push(subgroup);
          prevSubgroup = table[i].subgroup;
        } else {
          let lenSubgroup = -1;
          if (
            matrix[lenMatrix - 1].groups &&
            matrix[lenMatrix - 1].groups.length > 0 &&
            matrix[lenMatrix - 1].groups[lenGroup - 1].subgroups
          ) {
            lenSubgroup =
              matrix[lenMatrix - 1].groups[lenGroup - 1].subgroups.length;
          }

          if (lenSubgroup <= 0) {
            if (lenGroup <= 0) {
              matrix[lenMatrix - 1].params.push(table[i]);
            } else {
              matrix[lenMatrix - 1].groups[lenGroup - 1].params.push(table[i]);
            }
          } else {
            matrix[lenMatrix - 1].groups[lenGroup - 1].subgroups[
              lenSubgroup - 1
            ].params.push(table[i]);
          }
        }
      }

      function parseGroup(lenMatrix: number, into: string) {
        if (prevGroup !== table[i].group) {
          // Continua el Módulo, grupo nuevo
          prevSubgroup = '';
          arrSG = [];
          params = [];
          const group = {
            group: table[i].group as string,
            subgroups: arrSG,
            params,
          };
          matrix[lenMatrix - 1].groups.push(group);
          parseSubgroupInGroup(lenMatrix);
          prevGroup = table[i].group;
        } else {
          if (into === 'M') {
            parseSubgroupInModule(lenMatrix);
          } else {
            parseSubgroupInGroup(lenMatrix);
          }
        }
      }

      for (i = 0; i < table.length; i++) {
        if (prevModule !== table[i].module) {
          //Módulo nuevo
          prevGroup = '';
          prevSubgroup = '';
          params = [];
          arrSG = [];
          const arrGroup: {
            group: string;
            subgroups: { subgroup: string; params: unknown[] }[];
            params;
          }[] = [];
          matrix.push({ module: table[i].module, groups: arrGroup, params });

          const lenMatrix = matrix.length;
          parseGroup(lenMatrix, 'M');

          prevModule = table[i].module;
        } else {
          // Continua el Módulo
          const lenMatrix = matrix.length;
          parseGroup(lenMatrix, 'G');
        }
      }
      return matrix;
    },
    [matrix],
  );

  const getConfigParamTable = useCallback(() => {
    new Promise((resolve, reject) => {
      setShowPreloader(true);
      dashboard({ page: 1 }).then(resp => {
        setShowPreloader(false);
        if (resp.respCode === 0) {
          setConfigParamTable(fixParamTable(resp.data.params));
        }
      });
    });
  }, [dashboard, fixParamTable]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleUpdate = paramToUpdate => {
    setOpenBackdrop(true);
    //alert(JSON.stringify(paramToUpdate));
    updateParamValue(paramToUpdate)
      .then(resp => {
        setTimeout(() => {
          setOpenBackdrop(false);
        }, 500);
      })
      .catch(resp => {
        setShowPreloader(false);
        setAlertTitle('Actualizar Parámetro');
        setAlertMessage(resp.message);
        setOpen(true);
      });
  };

  useEffect(() => {
    setShowPreloader(true);
    getConfigParamTable();
  }, [setShowPreloader, dashboard, getConfigParamTable]);

  const { t } = useTranslation();

  const classes = useStyles();

  type ModuleType = { module: string; params: unknown[]; groups: unknown[] };

  return (
    <Container component="main" maxWidth="xl">
      <Helmet>
        <title>{t(messages.i18nPageTitle())}</title>
        <meta name="description" content="{t(messages.i18nDescription())}" />
      </Helmet>

      <CssBaseline />
      <div className={classes.paper}>
        <Typography component="h1" variant="h5">
          Parámetros del Sistema
        </Typography>
        <Typography
          component="h1"
          variant="body1"
          style={{
            width: '60%',
            margin: '30px 0 20px 0',
          }}
        >
          Los cambios que realice no se pueden deshacer, haga las modificaciones
          con cuidado. Los cambio se guardan y quedan activos automáticamente.
        </Typography>
        {showPreloader ? <PreLoader /> : null}

        <Box
          sx={{ bgcolor: 'background.paper', width: '100%' }}
          className={classes.moduleTab}
        >
          <AppBar position="static">
            <Tabs
              value={value}
              onChange={handleChange}
              indicatorColor="secondary"
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
              aria-label="scrollable auto tabs example"
            >
              {configParamTable.map((_module, index, array) => {
                const mod: ModuleType = _module as ModuleType;
                return (
                  <Tab label={mod.module} {...a11yProps(index)} key={index} />
                );
              })}
            </Tabs>
          </AppBar>

          <SwipeableViews
            axis={'x-reverse'}
            index={value}
            onChangeIndex={handleChangeIndex}
          >
            {configParamTable.map((_module, index, array) => {
              const mod: ModuleType = _module as ModuleType;
              return (
                <TabPanel value={value} index={index} key={index}>
                  <Box
                    sx={{
                      flexGrow: 1,
                      bgcolor: 'background.paper',
                      display: 'flex',
                    }}
                    className={classes.subgroupTab}
                  >
                    <Tabs
                      orientation="vertical"
                      variant="scrollable"
                      value={groupValue}
                      onChange={handleChangeGroup}
                      aria-label="Vertical tabs example"
                      className={classes.groupTab}
                    >
                      {mod.groups.map((group, index2, array2) => {
                        const _group = group as { group: string };
                        return (
                          <Tab
                            label={_group.group}
                            {...verticalA11yProps(index2)}
                            key={index2}
                          />
                        );
                      })}
                      {mod.params.length > 0 ? (
                        <Tab
                          label="General"
                          {...verticalA11yProps(mod.params.length)}
                          key={mod.groups.length}
                        />
                      ) : null}
                    </Tabs>

                    {mod.groups.map((group, index2, array2) => {
                      return (
                        <VerticalTabPanel
                          value={groupValue}
                          index={index2}
                          key={index2}
                          style={{ position: 'relative', left: '20px' }}
                        >
                          <GroupSection
                            key={'gs-' + index2}
                            moduleName={mod.module}
                            group={group}
                            updateParam={handleUpdate}
                          />
                        </VerticalTabPanel>
                      );
                    })}
                    {mod.params.length > 0 ? (
                      <VerticalTabPanel
                        value={groupValue}
                        index={mod.groups.length}
                        style={{ position: 'relative', left: '20px' }}
                      >
                        {(mod as {
                          module: string;
                          params: unknown[];
                        }).params.map((param, index2, array2) => {
                          return (
                            <ParamForm
                              key={index2}
                              item={param}
                              updateParam={handleUpdate}
                            />
                          );
                        })}
                      </VerticalTabPanel>
                    ) : null}
                  </Box>
                </TabPanel>
              );
            })}
          </SwipeableViews>
        </Box>
        <Backdrop
          ref={backdropRef}
          className={classes.backdrop}
          open={openBackdrop}
          onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </div>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{alertTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {alertMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained" autoFocus>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
